// Pestaña "Directivas" (cerebro, etapa 3): reglas de "qué mostrar" de la sección "Reglas que el
// cerebro propone", del cuaderno de preguntas vigente y de la columna "Terminó bien (7 d)" de
// Decisiones. Sin React, para probarlas sueltas (scripts/rules-view.test.ts). Nunca se muestra un
// nombre interno: ante la duda, "Otro" o un texto fijo en español. Sin dinero.
import { nextList, type Fetched } from './brainView';

export const RULES_FAIL_KEPT =
  'No se pudo leer la lista de reglas propuestas. Se muestra la última que se pudo leer.';
export const RULES_FAIL_NEVER = 'No se pudo leer la lista de reglas propuestas.';
export const APPROVE_FAIL = 'No se pudo aprobar la regla. Probá de nuevo en un momento.';
export const REJECT_FAIL = 'No se pudo rechazar la regla. Probá de nuevo en un momento.';
export const STRIKE_FAIL = 'No se pudo tachar la entrada. Probá de nuevo en un momento.';
/** Aclaración debajo de los ejemplos del "Por qué". */
export const EVIDENCE_NOTE = 'Los textos de los ejemplos vienen recortados.';

/** Tope de letras de una regla: el mismo que valida el servidor. */
export const RULE_TEXT_MAX = 300;

export const CATEGORY_LABEL: Record<string, string> = {
  estructura: 'Forma de la respuesta',
  tono: 'Tono',
  politica: 'Política / reglas del país',
  dato: 'Dato del producto',
  caso: 'Caso puntual',
};
export const PROFILE_LABEL: Record<string, string> = {
  questions: 'Preguntas',
  post_sale: 'Mensajes a compradores',
};
/** Motivos del "No": los mismos valores que acepta el servidor. */
export const REJECT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'no_es_regla_general', label: 'No es una regla general' },
  { value: 'dato_equivocado', label: 'Tiene un dato equivocado' },
  { value: 'ya_existe', label: 'Ya existe' },
  { value: 'otro', label: 'Otro' },
];
export const OUTCOME_LABEL: Record<string, string> = {
  bien: 'Terminó bien',
  neutro: 'Sin novedad',
  mal: 'Terminó mal',
  sin_dato: 'Sin dato',
};

const has = (m: Record<string, string>, k: string) => Object.prototype.hasOwnProperty.call(m, k);
const pick = (m: Record<string, string>, k: string | null | undefined) =>
  k != null && has(m, k) ? m[k] : 'Otro';
export const categoryLabel = (k: string | null | undefined) => pick(CATEGORY_LABEL, k);
export const profileLabel = (k: string | null | undefined) => pick(PROFILE_LABEL, k);
export const outcomeLabel = (k: string | null | undefined) => pick(OUTCOME_LABEL, k);

/** "Todas las empresas" si la regla es general; si no, el nombre de la empresa. */
export function scopeLabel(r: { seller_id: number | null; seller_name?: string | null }): string {
  if (r.seller_id == null) return 'Todas las empresas';
  const n = r.seller_name?.trim();
  return n ? n : `Empresa ${r.seller_id}`;
}

const txt = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/**
 * Hasta 3 ejemplos del "Por qué": "Pregunta: … → Propuesto: … → Lo que mandaste: …". En una
 * regla de posventa (o un ejemplo de posventa) lo primero es el resumen de la conversación:
 * "Conversación: …". Las partes vacías se omiten; un ejemplo sin nada se descarta; cualquier otra
 * forma → lista vacía.
 */
export function evidenceLines(c: { evidence: unknown; profile?: string }): string[] {
  let v: unknown = c.evidence;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const e of v) {
    if (!e || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    const parts = [
      txt(o.question) &&
        `${c.profile === 'post_sale' || o.kind === 'post_sale' ? 'Conversación' : 'Pregunta'}: ${txt(o.question)}`,
      txt(o.proposed) && `Propuesto: ${txt(o.proposed)}`,
      txt(o.final) && `Lo que mandaste: ${txt(o.final)}`,
    ].filter((p): p is string => !!p);
    if (parts.length === 0) continue;
    out.push(parts.join(' → '));
    if (out.length === 3) break;
  }
  return out;
}

/** Mismas reglas que el servidor para el texto de una regla (sin dirección de internet). */
const URL_RE = /https?:\/\/|www\./i;
export function editProblem(text: string): string | null {
  const t = text.trim();
  if (!t) return 'El texto está vacío.';
  if (t.length < 3) return 'El texto es demasiado corto.';
  if (t.length > RULE_TEXT_MAX) return `Se pasa del tope: ${t.length} de ${RULE_TEXT_MAX} letras.`;
  if (URL_RE.test(t)) return 'La regla no puede llevar direcciones de internet.';
  return null;
}

/** "Sí con cambios": texto a mandar, o undefined si (sin bordes) es igual al propuesto. */
export function ruleTextToSend(edited: string, proposed: string): string | undefined {
  return edited.trim() === proposed.trim() ? undefined : edited.trim();
}

/** Una fila del resumen de resultados (control a 24 h o a 7 días) por tipo y situación. */
export interface OutcomeRow {
  kind: string;
  situation: string | null;
  check: string;
  bien: number;
  neutro: number;
  mal: number;
  sin_dato: number;
}

/** Porcentaje de "terminó bien" sobre los controles con dato (los "sin dato" no cuentan). */
export function outcomePct(
  rows: ReadonlyArray<Pick<OutcomeRow, 'bien' | 'neutro' | 'mal'>>
): { pct: number; bien: number; withData: number } | null {
  let bien = 0;
  let withData = 0;
  for (const r of rows) {
    const b = Number(r.bien) || 0;
    bien += b;
    withData += b + (Number(r.neutro) || 0) + (Number(r.mal) || 0);
  }
  if (withData === 0) return null;
  return { pct: Math.round((bien / withData) * 100), bien, withData };
}

/** Celda "Terminó bien (7 d)" para un tipo y situación: "75 % (3 de 4)" o "—" si no hay dato. */
export function outcomeCell(
  rows: ReadonlyArray<OutcomeRow> | null | undefined,
  kind: string,
  situation: string | null
): string {
  const mine = (rows ?? []).filter(
    (r) => r.check === '7d' && r.kind === kind && (r.situation ?? null) === (situation ?? null)
  );
  const p = outcomePct(mine);
  return p ? `${p.pct} % (${p.bien} de ${p.withData})` : '—';
}

/**
 * Textos de otras capas del servidor que vienen en inglés: nunca se muestran, va el texto fijo.
 * Las rutas de reglas responden siempre en español: esos se muestran tal cual.
 */
const ENGLISH_TEXTS = new Set([
  'forbidden',
  'Authentication required',
  'Session expired',
  'Access denied: seller mismatch',
  'Not found',
  'Internal server error',
]);

/** Texto del fallo: el del servidor tal cual; el fijo si llegó vacío, en inglés o la llamada lanzó. */
export function serverText(f: Fetched<unknown>, fallback: string): string | null {
  if (f.kind === 'ok') return null;
  const e = f.kind === 'server_error' ? f.error?.trim() : '';
  if (!e || ENGLISH_TEXTS.has(e)) return fallback;
  return e;
}

/** Tras un refresco de la lista: si falla se conserva la última buena. */
export function nextRules<T>(prev: T | null, f: Fetched<T>) {
  return nextList(prev, f, { kept: RULES_FAIL_KEPT, never: RULES_FAIL_NEVER });
}

/** Una entrada del cuaderno de reglas del ayudante de preguntas. */
export interface FaqItem {
  id: string;
  categoria: string;
  text: string;
  scope: 'global' | number;
  tachada: boolean;
  seller_name?: string;
}

/** Agrupa el cuaderno: primero "Todas las empresas", después cada empresa. */
export function groupFaq(items: FaqItem[]): Array<{ key: string; title: string; items: FaqItem[] }> {
  const groups = new Map<string, { key: string; title: string; items: FaqItem[] }>();
  const global = items.filter((i) => i.scope === 'global');
  const out: Array<{ key: string; title: string; items: FaqItem[] }> = [];
  if (global.length > 0) out.push({ key: 'global', title: 'Todas las empresas', items: global });
  for (const i of items) {
    if (i.scope === 'global') continue;
    const key = String(i.scope);
    let g = groups.get(key);
    if (!g) {
      g = { key, title: i.seller_name?.trim() || `Empresa ${i.scope}`, items: [] };
      groups.set(key, g);
      out.push(g);
    }
    g.items.push(i);
  }
  return out;
}
